import React, { useEffect, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { EventData, UpcommingMeetingDto } from '../../../../interfaces/identity/calendar/Events';
import { updateEvent } from '../../../../services/calendarService/EventService';
import { toast } from 'react-toastify';
import moment from 'moment';
import editicons from '../../../../assets/images/editicons.svg';
import { useNavigate } from 'react-router-dom';
import { start } from 'repl';
import isEqual from 'lodash/isEqual';

interface RescheduleMeetingModalProps {
    handleCloseRescheduleModal: () => void;
    rescheduleModal: boolean;
    authToken: string;
    selectedMeeting: UpcommingMeetingDto;
    refreshMeeting: (token: string) => void;
}

const RescheduleMeetingModal = ({ handleCloseRescheduleModal, rescheduleModal, authToken, selectedMeeting, refreshMeeting }: RescheduleMeetingModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [date, setDate] = useState<string>('');
    const [startTime, setStartTime] = useState<string>('');
    const [endTime, setEndTime] = useState<string>('');
    const [validated, setValidated] = useState<boolean>(false);
    const [initialValues, setInitialValues] = useState<{ date: string; startTime: string; endTime: string } | null>(null);
    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const navigate = useNavigate();


    // Initialize state from props
    useEffect(() => {
        if (selectedMeeting?.startTime && selectedMeeting?.endTime) {
            const start = moment.utc(selectedMeeting.startTime).local();
            const end = moment.utc(selectedMeeting.endTime).local();

            const d = start.format('YYYY-MM-DD');
            const s = start.format('HH:mm');
            const e = end.format('HH:mm');

            setDate(d);
            setStartTime(s);
            setEndTime(e);

            setInitialValues({ date: d, startTime: s, endTime: e });
        } else {
            setDate('');
            setStartTime('');
            setEndTime('');
            setInitialValues(null);
        }
    }, [selectedMeeting]);

    const updateMeeting = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const startDateTime = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
        const endDateTime = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm').toDate();
        if (startDateTime >= endDateTime) {
            toast.error("Start time must be before end time");
            return;
        }

        const form = e.currentTarget;
        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }
        setValidated(true);
        setLoading(true);

        const timezone = currentTimeZone;
        const startTime24hr = moment(startTime, "h:mm A").format("HH:mm");
        const endTime24hr = moment(endTime, "h:mm A").format("HH:mm");

        const payload = {
            title: selectedMeeting.title,
            startTime: moment.tz(`${date} ${startTime24hr}`, timezone).toISOString(),
            endTime: moment.tz(`${date} ${endTime24hr}`, timezone).toISOString(),
            description: selectedMeeting.description,
            location: selectedMeeting.location,
            meetingDetails: selectedMeeting.meetingDetails,
            ownerUserID: selectedMeeting.ownerUserID,
            participantUserIds: selectedMeeting.participantUsers.map(p => p.participantId),
            taskId: null,
            id: selectedMeeting.id,
            externalUserEmails: selectedMeeting.externalUserEmails
        };

        updateEvent(payload, authToken).then((response) => {
            if (response.success) {
                toast.success("Meeting Time Updated Successfully");
                refreshMeeting(authToken);
                handleCloseRescheduleModal();
            } else {
                toast.error(response.message);
            }
        }).catch((err) => {
            toast.error(err.message);
        }).finally(() => {
            setLoading(false);
        })
    };

    const onClickEdit = () => {
        handleCloseRescheduleModal();
        // const date = moment(selectedMeeting.startTime).format('YYYY-MM-DD');
        navigate(`/calendar?date=${date}T${startTime}`);
    }

    const hasChanges = initialValues ? !isEqual(initialValues, { date, startTime, endTime }) : false;

    return (
        <div>
            <Modal show={rescheduleModal} onHide={handleCloseRescheduleModal} className="my_modal" centered>
                <Form onSubmit={updateMeeting} noValidate validated={validated} >
                    <Modal.Header closeButton>
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <Modal.Title className="mb-0">Reschedule Meeting</Modal.Title>
                            <button onClick={onClickEdit}><img style={{ width: '24px' }} src={editicons} alt="editicons" /></button>
                        </div>
                    </Modal.Header>

                    <Modal.Body>
                        <Form.Group controlId="formDate" className="w-full mb-3">
                            <Form.Label className="w-28">Select Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={date}
                                min={moment().format("YYYY-MM-DD")}
                                onChange={(e) => setDate(e.target.value)}
                                required
                            />
                            <Form.Control.Feedback type='invalid'>
                                Please select a date
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formStartTime" className="w-full mb-3">
                            <Form.Label>Start Time</Form.Label>
                            <Form.Control
                                type='time'
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                required
                            />
                            <Form.Control.Feedback type='invalid'>
                                Please enter start time
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formEndTime" className="w-full mb-3 mb-0">
                            <Form.Label>End Time</Form.Label>
                            <Form.Control
                                type='time'
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                required
                            />
                            <Form.Control.Feedback type='invalid'>
                                Please enter end time
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseRescheduleModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading || !hasChanges}>
                            {loading ? (
                                <>
                                    <Spinner size="sm"/>
                                </>
                            ) : (
                                'Update'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    )
}

export default RescheduleMeetingModal;
